import { Button, Paper, Stack } from '@mantine/core';
import { IconPencilMinus, IconCheckbox } from '@tabler/icons-react';

import { WindowStatusEnum } from '@/api';

import { SubmissionState, WindowStatus } from '../types';
import type { Submission } from '../types';

import CancellationCheckbox from './AvailabilityCalendar.cancellation-checkbox';
import DeadlineBanner from './AvailabilityCalendar.deadline-banner';

type SubmitSectionProps = {
  windowStatus: WindowStatus;
  submissionState: SubmissionState;
  submission: Submission | null;
  acknowledgementChecked: boolean;
  onAcknowledgementChange: (checked: boolean) => void;
  onSubmit: () => void;
  onEdit: () => void;
  onCancel: () => void;
  submitLoading: boolean;
  isDirty: boolean;
  deadlineDate: string | null;
  requiresSubmission: boolean;
  showsEditButton: boolean;
};

const SubmitSection: React.FC<SubmitSectionProps> = ({
  submissionState,
  acknowledgementChecked,
  onAcknowledgementChange,
  onSubmit,
  onEdit,
  onCancel,
  submitLoading,
  windowStatus,
  showsEditButton,
  deadlineDate,
  requiresSubmission,
}) => {
  const isLocked = windowStatus === WindowStatusEnum.LOCKED;

  const isInitialSubmission =
    requiresSubmission && submissionState === SubmissionState.NOT_SUBMITTED;

  const isEditing = submissionState === SubmissionState.EDITING;

  const submitDisabled = !acknowledgementChecked || submitLoading;
  const editingButtonLabel = requiresSubmission
    ? 'Submit updated availability'
    : 'Update availability';

  const banner = (
    <DeadlineBanner
      deadlineDate={deadlineDate}
      windowStatus={windowStatus}
      requiresSubmission={requiresSubmission}
    />
  );

  if (isLocked) {
    return (
      <Stack gap="sm">
        <Button
          fullWidth
          disabled
          variant="outline"
          color="gray"
          size="md"
          rightSection={<IconPencilMinus size={16} />}
        >
          Edit availability
        </Button>
        {banner}
      </Stack>
    );
  }

  if (isInitialSubmission) {
    return (
      <Paper p="lg" radius="md" shadow="sm" withBorder>
        <Stack gap="sm">
          <CancellationCheckbox
            checked={acknowledgementChecked}
            onChange={onAcknowledgementChange}
          />
          <Button
            fullWidth
            disabled={submitDisabled}
            loading={submitLoading}
            onClick={onSubmit}
            size="md"
            color="blue"
            rightSection={<IconCheckbox size={16} />}
          >
            Submit availability
          </Button>
          {banner}
        </Stack>
      </Paper>
    );
  }

  if (showsEditButton) {
    return (
      <Stack gap="sm">
        <Button variant="outline" onClick={onEdit} rightSection={<IconPencilMinus size={16} />}>
          Edit Availability
        </Button>
        {banner}
      </Stack>
    );
  }

  if (isEditing) {
    return (
      <Paper p="lg" radius="md" shadow="md" withBorder>
        <Stack gap="sm">
          <CancellationCheckbox
            checked={acknowledgementChecked}
            onChange={onAcknowledgementChange}
          />
          <Button
            disabled={submitDisabled}
            loading={submitLoading}
            onClick={onSubmit}
            size="md"
            color="blue"
            rightSection={<IconCheckbox size={16} />}
          >
            {editingButtonLabel}
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          {banner}
        </Stack>
      </Paper>
    );
  }

  return null;
};

export default SubmitSection;
